import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import Placeholder from 'images/placeholder1080x1080.png'
import ProductContext from '../context/ProductContext'
import FavoritesBtnView from '@components/Product/FavoritesBtn/FavoritesBtnView'
import { getLightboxUiElements } from '../../../utils/imagesLightbox'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { WholesaleAppStatusEnum } from '../../../../generated/gqlTypes'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material'
import { Swiper as SwiperClass } from 'swiper/types'
import { getYoutubeThumb } from '../../../utils/misc'
import ReactPlayer from 'react-player'

const DEFAULT_IMAGES = [
  {
    id: 'default_img',
    thumbnail: Placeholder,
    src: Placeholder,
    alt: '',
    width: 1080,
    height: 1080,
  },
]

interface IProps {
  onGooglePushProductDetails(): void
}

const ProductImages: React.FC<IProps> = ({ onGooglePushProductDetails }) => {
  const { me, reflection, images } = useContext(ProductContext)
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'))
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'))
  const swiperRef = useRef<SwiperClass | null>(null)

  const [thumbsSwiperSm, setThumbsSwiperSm] = useState<SwiperClass | null>(null)
  const [thumbsSwiperLg, setThumbsSwiperLg] = useState<SwiperClass | null>(null)

  const productPk = reflection?.product?.pk
  const onOpenViewer = e => {
    e.preventDefault()
    onGooglePushProductDetails()
  }
  const pageImages = images.length
    ? images.map(node => ({
        id: node.id,
        thumbnail: node.thumbnail,
        src: node.url,
        alt: node.alt || '',
        width: node.width,
        height: node.height,
      }))
    : DEFAULT_IMAGES

  useEffect(() => {
    if (swiperRef?.current?.slideTo) {
      swiperRef.current.slideTo(0)
    }
    if (thumbsSwiperSm?.slideTo) {
      thumbsSwiperSm.slideTo(0)
    }
    if (thumbsSwiperLg?.slideTo) {
      thumbsSwiperLg.slideTo(0)
    }
  }, [images, swiperRef])

  const isEmpty = !images.length
  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      spaceBetween: 0,
      slidesPerView: 1,
      lazy: false,
      preloadImages: true,
      pagination: {
        clickable: true,
        el: '#swiper-pagination',
      },
      watchSlidesProgress: true,
      autoHeight: true,
      touchMoveStopPropagation: true,
      preventClicksPropagation: true,
    }),
    []
  )
  const videoUrl = reflection?.videoUrl
  const productIsInFavorites = reflection?.product?.isInFavorites
  const withDownloadButton = me?.isStaff || me?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed

  const slides = pageImages.map(img => ({ image: img, video: null }))

  if (videoUrl) {
    slides.splice(1, 0, { image: null, video: videoUrl })
  }

  return (
    <div className='sticky product-images-swiper' style={{ top: '120px' }}>
      <div className={!smUp ? '' : 'hidden'}>
        {!!pageImages.length && (
          <>
            <Gallery
              uiElements={getLightboxUiElements()}
              options={{ loop: false }}
              withDownloadButton={withDownloadButton}
            >
              <Swiper {...swiperOptions}>
                {pageImages.map((img, i) => {
                  return (
                    <SwiperSlide key={img.id}>
                      <Item
                        original={img.src}
                        thumbnail={img.thumbnail}
                        width={img.width}
                        height={img.height}
                        alt={img.alt}
                      >
                        {({ ref, open }) => (
                          <div
                            className='w-full h-full flex items-center justify-center'
                            style={{ maxWidth: '100%', maxHeight: '800px' }}
                          >
                            <img
                              className='cursor-pointer max-w-full'
                              height={isEmpty ? 400 : 'auto'}
                              ref={ref as React.MutableRefObject<HTMLImageElement>}
                              src={img.thumbnail}
                              alt={img.alt}
                              onClick={e => {
                                onOpenViewer(e)
                                open(e)
                              }}
                            />
                          </div>
                        )}
                      </Item>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </Gallery>
            {!!me && <FavoritesBtnView isInFavorites={productIsInFavorites} pk={productPk} />}
            <div
              id='swiper-pagination'
              className='swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full my-5'
            />
          </>
        )}
        {videoUrl && (
          <div className='px-0 md:px-30'>
            <ReactPlayer src={videoUrl} style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }} controls />
          </div>
        )}
      </div>

      <div className={smUp ? '' : 'hidden'}>
        <div className='absolute w-[80px] swiper-thumbs' style={{ display: lgUp ? 'block' : 'none' }}>
          <Swiper
            direction='vertical'
            onSwiper={setThumbsSwiperLg}
            spaceBetween={5}
            slidesPerGroup={3}
            slidesPerView='auto'
            watchSlidesProgress={true}
            className='max-h-[800px]'
            navigation={{
              nextEl: '.swiper-button-next-thumb',
              prevEl: '.swiper-button-prev-thumb',
            }}
            freeMode={{
              enabled: true,
              momentum: true,
              momentumBounce: false,
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 0.4,
              thresholdDelta: 3,
              releaseOnEdges: false,
            }}
          >
            {/* Top (Prev) arrow */}
            <div className='swiper-button-prev-thumb' aria-label='Previous' />

            {/* Bottom (Next) arrow */}
            <div className='swiper-button-next-thumb' aria-label='Next' />
            {slides.map(slide => {
              if (slide.video) {
                return (
                  <SwiperSlide key={slide.video}>
                    <img
                      className='cursor-pointer max-w-full object-contain w-full h-full rounded-lg'
                      src={getYoutubeThumb(slide.video)}
                      alt={slide.video}
                    />
                  </SwiperSlide>
                )
              }
              const img = slide.image
              return (
                <SwiperSlide key={img.id}>
                  <img
                    className='cursor-pointer max-w-full object-contain w-full h-full rounded-lg'
                    src={img.thumbnail}
                    alt={img.alt}
                  />
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
        <div className={lgUp ? 'pl-[90px]' : ''}>
          <Gallery
            uiElements={getLightboxUiElements()}
            options={{ loop: false }}
            withDownloadButton={withDownloadButton}
          >
            <Swiper
              key={lgUp ? 'lg' : 'sm'}
              thumbs={{
                swiper: lgUp ? thumbsSwiperLg || null : thumbsSwiperSm || null,
              }}
              spaceBetween={0}
              onSwiper={swiper => (swiperRef.current = swiper)}
              className='swiper-slides-center'
              navigation
              autoHeight={!lgUp}
            >
              {slides.map((slide, i) => {
                if (slide.video) {
                  return (
                    <SwiperSlide key={slide.video}>
                      <ReactPlayer
                        src={slide.video}
                        style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }}
                        controls
                      />
                    </SwiperSlide>
                  )
                }
                const img = slide.image
                return (
                  <SwiperSlide key={img.id}>
                    <Item
                      original={img.src}
                      thumbnail={img.thumbnail}
                      width={img.width}
                      height={img.height}
                      alt={img.alt}
                    >
                      {({ ref, open }) => (
                        <div
                          className='w-full h-full flex items-center justify-center'
                          style={{ maxWidth: '100%', maxHeight: '800px' }}
                        >
                          <img
                            className='max-w-full max-h-full cursor-pointer'
                            style={{ maxHeight: '800px' }}
                            ref={ref as React.MutableRefObject<HTMLImageElement>}
                            src={img.thumbnail}
                            alt={img.alt}
                            onClick={e => {
                              onOpenViewer(e)
                              open(e)
                            }}
                          />
                        </div>
                      )}
                    </Item>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </Gallery>

          {!!me && <FavoritesBtnView isInFavorites={productIsInFavorites} pk={productPk} />}
        </div>

        <div className='swiper-thumbs' style={{ display: !lgUp ? 'block' : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Swiper
              direction='horizontal'
              onSwiper={setThumbsSwiperSm}
              spaceBetween={5}
              slidesPerView='auto'
              className='mt-10'
              style={{ width: 'auto', height: '60px' }}
              freeMode
              watchSlidesProgress
            >
              {slides.map(slide => {
                if (slide.video) {
                  return (
                    <SwiperSlide key={slide.video} style={{ width: '60px' }}>
                      <img
                        className='cursor-pointer max-w-full object-contain w-full h-full rounded-lg'
                        src={getYoutubeThumb(slide.video)}
                        alt={slide.video}
                      />
                    </SwiperSlide>
                  )
                }
                const img = slide.image
                return (
                  <SwiperSlide key={img.id} style={{ width: '60px' }}>
                    <img
                      className='cursor-pointer max-w-full object-contain w-full h-full rounded-lg'
                      src={img.thumbnail}
                      alt={img.alt}
                    />
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(ProductImages)
